import type { DecorationAssetFailure, DecorationAssetsPrepared, SWMessage } from "@innoclouds/service-worker/messages";
import { type Decoration, type DecorationAsset, type DecorationPayload, isRecord, sanitizeFontFamilies } from "@innoclouds/ui";
import { captureException } from "@sentry/vue";
import { getActiveWorker } from "#/composables/useSync";
import { DECORATION_KEY } from "#/store/decoration";
import { loadLocalFonts } from "./localFonts";

const PREPARE_TIMEOUT_MS = 60_000;
const FONT_SLOTS = ["main", "light", "bold"] as const;

export interface ApplyDecorationOptions {
	precache?: boolean;
	isCurrent?: () => boolean;
}

type Preparation = { status: "skipped" } | { status: "retry" } | { status: "ready"; worker: ServiceWorker };

type StoreResult = "written" | "unchanged" | "failed";

type RemoveResult = "removed" | "absent" | "failed";

const SKIPPED: Preparation = { status: "skipped" };
const RETRY: Preparation = { status: "retry" };

const reportedAssets = new Set<string>();
let persistenceRequested = false;

export const reportDecorationError = (error: unknown, source: string, extra?: Record<string, unknown>) => {
	console.warn(`[${source}]`, error);
	captureException(error, { level: "warning", tags: { source }, extra });
};

const isHttpUrl = (value: unknown): value is string => {
	if (typeof value !== "string" || !value) return false;
	try {
		return /^https?:$/.test(new URL(value, location.href).protocol);
	} catch {
		return false;
	}
};

const urlOf = (file: unknown) => (isRecord(file) ? file.url : undefined);

const familyUrls = (fonts: Record<string, unknown>) => (sanitizeFontFamilies(fonts.families) ?? []).flatMap((family) => family.files.map((file) => file.url));

const fontUrls = (fonts: unknown) => (isRecord(fonts) ? [...FONT_SLOTS.map((slot) => urlOf(fonts[slot])), ...familyUrls(fonts)] : []);

const screenIconUrls = (elements: Record<string, unknown>) =>
	Object.values(elements)
		.filter(isRecord)
		.map((element) => urlOf(element.icon));

const iconUrls = (screens: unknown) => (isRecord(screens) ? Object.values(screens).filter(isRecord).flatMap(screenIconUrls) : []);

const toAssets = (type: DecorationAsset["type"], urls: unknown[]): DecorationAsset[] => urls.filter(isHttpUrl).map((url) => ({ type, url }));

export const assetsOf = (decoration: Decoration | null | undefined): DecorationAsset[] =>
	isRecord(decoration) ? [...toAssets("font", fontUrls(decoration.fonts)), ...toAssets("icon", iconUrls(decoration.screens))] : [];

const ask = (worker: ServiceWorker, message: SWMessage, timeoutMs: number) =>
	new Promise<MessageEvent | null>((resolve) => {
		const { port1, port2 } = new MessageChannel();
		const settle = (reply: MessageEvent | null) => {
			window.clearTimeout(timer);
			port1.close();
			resolve(reply);
		};
		const timer = window.setTimeout(() => settle(null), timeoutMs);
		port1.onmessage = settle;
		try {
			worker.postMessage(message, [port2]);
		} catch {
			settle(null);
		}
	});

const isPreparedReply = (data: unknown): data is DecorationAssetsPrepared =>
	isRecord(data) && data.type === "DECORATION_ASSETS_PREPARED" && typeof data.ok === "boolean" && Array.isArray(data.missing);

const reportPermanentFailure = ({ url, status }: DecorationAssetFailure) => {
	if (reportedAssets.has(url)) return;
	reportedAssets.add(url);
	reportDecorationError(new Error(`Decoration asset unavailable: ${url}`), "decoration.asset", { url, status });
};

const reportMissingAssets = (missing: DecorationAssetFailure[]) => {
	for (const failure of missing) {
		if (failure.permanent) reportPermanentFailure(failure);
		else console.warn("[decoration] asset temporarily unavailable, keeping current decoration", failure.url, failure.status ?? "");
	}
};

const prepareAssets = async (assets: DecorationAsset[]): Promise<Preparation> => {
	const worker = await getActiveWorker();
	if (!worker) return SKIPPED;
	const reply = await ask(worker, { type: "DECORATION_ASSETS_PREPARE", assets }, PREPARE_TIMEOUT_MS);
	if (!reply) return RETRY;
	if (!isPreparedReply(reply.data)) return SKIPPED;
	reportMissingAssets(reply.data.missing);
	return reply.data.ok ? { status: "ready", worker } : RETRY;
};

const commitAssets = (worker: ServiceWorker, assets: DecorationAsset[]) => {
	try {
		worker.postMessage({ type: "DECORATION_ASSETS_COMMIT", urls: assets.map((asset) => asset.url) } satisfies SWMessage);
	} catch (error) {
		console.warn("[decoration] asset commit not delivered", error);
	}
};

const pruneCachedAssets = async () => {
	const worker = await getActiveWorker();
	if (worker) commitAssets(worker, []);
};

const requestPersistentStorage = () => {
	if (persistenceRequested) return;
	persistenceRequested = true;
	void navigator.storage?.persist?.().catch(() => false);
};

const storeDecoration = (decoration: Decoration): StoreResult => {
	const next = JSON.stringify(decoration);
	try {
		if (localStorage.getItem(DECORATION_KEY) === next) return "unchanged";
		localStorage.setItem(DECORATION_KEY, next);
		return "written";
	} catch (error) {
		reportDecorationError(error, "decoration.storage");
		return "failed";
	}
};

export const applyDecorationPayload = async (
	{ decoration, assets }: DecorationPayload,
	{ precache = true, isCurrent = () => true }: ApplyDecorationOptions = {},
): Promise<boolean> => {
	const files = assets ?? assetsOf(decoration);
	const preparation = precache ? await prepareAssets(files) : SKIPPED;
	if (preparation.status === "retry" || !isCurrent()) return false;

	const stored = storeDecoration(decoration);
	if (stored === "failed") return false;
	if (preparation.status === "ready") {
		commitAssets(preparation.worker, files);
		requestPersistentStorage();
	}
	if (stored === "unchanged") return false;

	void loadLocalFonts();
	return true;
};

const removeDecoration = (): RemoveResult => {
	try {
		if (localStorage.getItem(DECORATION_KEY) === null) return "absent";
		localStorage.removeItem(DECORATION_KEY);
		return "removed";
	} catch (error) {
		reportDecorationError(error, "decoration.storage");
		return "failed";
	}
};

export const clearDecorationPayload = () => {
	const removed = removeDecoration();
	if (removed === "failed") return false;
	void pruneCachedAssets();
	if (removed === "absent") return false;

	void loadLocalFonts();
	return true;
};
